import { useState, useEffect } from 'react';
import { Edit2, Trash2, Eye, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRouter } from '../components/Router';

interface Listing {
  id: string;
  brand: string;
  model: string;
  year: number;
  price: number;
  images: string[];
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  location?: string;
  mileage?: number;
  slug?: string;
}

export function MyListingsPage() {
  const { user, loading } = useAuth(); // Get loading state
  const { navigate } = useRouter();
  const [listings, setListings] = useState<Listing[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  useEffect(() => {
    // --- FIX: Wait for auth to finish loading ---
    if (loading) return;

    if (!user) {
      navigate('/connecter?redirect=/mes-annonces');
      return;
    }
    fetchListings();
  }, [user, loading]);

  const fetchListings = async () => {
    try {
      setDataLoading(true);
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (error: any) {
      console.error('Error fetching listings:', error);
      alert('Erreur lors du chargement des annonces');
    } finally {
      setDataLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette annonce? Cette action est irréversible.')) return;

    try {
      setDeleteLoading(id);
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setListings(listings.filter(l => l.id !== id));
      
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-50/90 backdrop-blur-xl border border-green-200 text-green-800 px-6 py-3 rounded-2xl z-50';
      successMsg.innerHTML = '<div class="flex items-center"><svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>Annonce supprimée avec succès</div>';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
      
    } catch (error: any) {
      console.error('Error deleting listing:', error);
      alert('Erreur lors de la suppression de l\'annonce');
    } finally {
      setDeleteLoading(null);
    }
  };

  // ... (getStatusBadge and getStatusMessage remain same as before) ...
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
            Approuvé
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
            <Clock className="h-3.5 w-3.5 mr-1" />
            En attente
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
            <XCircle className="h-3.5 w-3.5 mr-1" />
            Rejeté
          </span>
        );
      default:
        return null;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Votre annonce est en ligne et visible par tous les utilisateurs.';
      case 'pending':
        return 'Votre annonce est en cours de vérification. Cela prend généralement 24-48 heures.';
      case 'rejected':
        return 'Votre annonce a été rejetée. Veuillez la modifier et la soumettre à nouveau.';
      default:
        return '';
    }
  };

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D5462E] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Chargement de vos annonces...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-10 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">Mon Garage</h1>
              <p className="text-gray-500">Gérez vos annonces publiées</p>
            </div>
            <a
              href="/soumettre-annonce"
              className="mt-4 sm:mt-0 inline-flex items-center justify-center px-6 py-3 bg-[#D5462E] text-white rounded-full font-semibold hover:bg-[#b83a24] transition-all"
            >
              + Nouvelle annonce
            </a>
          </div>
        </div>

        {listings.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Eye className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune annonce</h3>
            <p className="text-gray-600 mb-6">Vous n'avez pas encore publié d'annonce. Commencez dès maintenant !</p>
            <a href="/soumettre-annonce" className="inline-block bg-[#D5462E] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#b83a24] transition-all">Publier une annonce</a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <div key={listing.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-all">
                <div className="relative h-48">
                  <img
                    src={listing.images && listing.images.length > 0 ? listing.images[0] : 'https://images.pexels.com/photos/3802510/pexels-photo-3802510.jpeg'}
                    alt={`${listing.brand} ${listing.model}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">{getStatusBadge(listing.status)}</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{listing.brand} {listing.model}</h3>
                  <div className="space-y-2 mb-4">
                    <p className="text-2xl font-semibold text-[#D5462E]">{listing.price.toLocaleString()} DT</p>
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <span>📅 {listing.year}</span>
                      {listing.mileage && <span>🛣️ {listing.mileage.toLocaleString()} km</span>}
                    </div>
                    {listing.location && <p className="text-sm text-gray-500">📍 {listing.location}</p>}
                  </div>
                  <div className={`text-xs p-3 rounded-2xl mb-4 ${listing.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-100' : listing.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                    {getStatusMessage(listing.status)}
                  </div>
                  <div className="flex gap-2">
                    <a href={`/annonces/${listing.slug || listing.id}`} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 rounded-2xl text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm"><Eye className="h-4 w-4" /> Voir</a>
                    <a href={`/modifier-annonce/${listing.id}`} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 rounded-2xl text-[#D5462E] hover:bg-[#D5462E]/10 transition-colors font-medium"><Edit2 className="h-4 w-4" /></a>
                    <button onClick={() => handleDelete(listing.id)} disabled={deleteLoading === listing.id} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 rounded-2xl text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium">{deleteLoading === listing.id ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent" /> : <Trash2 className="h-4 w-4" />}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {listings.length > 0 && (
          <div className="mt-10 bg-blue-50/50 backdrop-blur-xl border border-blue-100 rounded-3xl p-8">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Besoin d'aide?</h4>
                <p className="text-sm text-blue-800 leading-relaxed">Vos annonces sont vérifiées sous 24-48 heures. Si une annonce est rejetée, vous pouvez la modifier et la soumettre à nouveau.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}